import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import styles from './MeetingDetailsView.module.scss';
import Table, { ATTENDANCE_STATUS, MEETING_STATUS } from '../Table/Table/Table';
import useApiData from '../../../../hooks/useApiData';
import Loading from '../Loading/Loading';
import { getErrorComponentFromHttpError } from '../Error/Error';
import { getAbsoluteTimestamp } from '../../../../util/formatDate';
import EnumElement from '../Table/EnumElement/EnumElement';
import { DummyUserPortrait } from '../../User/Team/Meetings/Meeting/UserPortrait/UserPortrait';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import CommitmentsTableCommitment from './CommitmentsTableCommitment/CommitmentsTableCommitment';
import CommitmentsTeamMember from './CommitmentsTeamMember/CommitmentsTeamMember';
import { GroupsOutlined, EditCalendarOutlined } from '@mui/icons-material';
import {
  AdministratorEditMeetingStatusDialog,
  TeamEditMeetingStatusDialog,
} from '../EditMeetingStatusDialog/EditMeetingStatusDialog';
import {
  AdministratorEditMeetingAttendanceDialog,
  TeamEditMeetingAttendanceDialog,
} from '../EditMeetingAttendanceDialog/EditMeetingAttendanceDialog';
import Button from '@mui/material/Button';
import {
  AdministratorEditMeetingCommitmentDialog,
  TeamEditMeetingCommitmentDialog,
} from '../EditMeetingCommitmentDialog/EditMeetingCommitmentDialog';
import useAuth from '../../../../hooks/useAuth';
import APIClient from '../../../../util/APIClient';
import FormAlert from '../FormAlert/FormAlert';
import LoadingButton from '@mui/lab/LoadingButton';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';

function getAttendanceSortValue(a) {
  switch (a) {
    case 'JOINED':
      return 0;
    case 'PRESENT':
      return 1;
    case 'ABSENT':
      return 2;
    case 'AWOL':
      return 3;
    case 'UNKNOWN':
      return 10;
  }
}

export function attendanceSortFn(a, b) {
  const aValue = getAttendanceSortValue(a.attendance);
  const bValue = getAttendanceSortValue(b.attendance);

  if (aValue < bValue) {
    return -1;
  } else if (aValue > bValue) {
    return 1;
  }

  if (a.firstName < b.firstName) {
    return -1;
  } else if (a.firstName > b.firstName) {
    return 1;
  }

  if (a.lastName < b.lastName) {
    return -1;
  } else if (a.lastName > b.lastName) {
    return 1;
  }

  return 0;
}

export default function MeetingDetailsView(props) {
  const navigate = useNavigate();
  const { teamId, meetingId, isAdministratorView, useSecondaryHeadingLevel } = props;
  const { currentUser, groups } = useAuth();
  const [formAlert, setFormAlert] = React.useState(null);
  const [isSubmittingStartMeeting, setIsSubmittingStartMeeting] = React.useState(false);
  const [selectedCommitment, setSelectedCommitment] = React.useState(null);
  const [isEditMeetingCommitmentDialogOpen, setIsEditMeetingCommitmentDialogOpen] = React.useState(false);
  const [isEditMeetingStatusDialogOpen, setIsEditMeetingStatusDialogOpen] = React.useState(false);
  const [isEditMeetingAttendanceDialogOpen, setIsEditMeetingAttendanceDialogOpen] = React.useState(false);
  const { data, error, isLoading, reload } = useApiData({
    path: `/${isAdministratorView ? 'administrator' : 'user'}/teams/${teamId}/meetings/${meetingId}`,
    params: {
      includeTeam: true,
      includeTeamGoals: true,
      includeTeamMembers: true,
      includeCommitments: true,
      includeAttendances: true,
    },
  });

  async function onStartMeeting() {
    setIsSubmittingStartMeeting(true);
    try {
      await APIClient.post(`/user/teams/${teamId}/meetings/${meetingId}/start`);
      navigate(`/user/teams/${teamId}/meetings/${meetingId}`);
    } catch {
      setIsSubmittingStartMeeting(false);
      setFormAlert({
        severity: 'error',
        title: 'Could not start meeting!',
        message:
          'Something went wrong when attempting to start the meeting. Please contact the administrator for further support.',
      });
    }
  }

  const attendanceData = React.useMemo(() => {
    if (data === null || !Array.isArray(data?.attendances)) {
      return [];
    }
    return data.attendances
      .map((a) => {
        const teamMember = data.team.teamMembers.filter((t) => t.cognitoId === a.cognitoId)?.[0];
        const { firstName = '?', lastName = '?' } = teamMember || {};

        return {
          ...a,
          portrait: <DummyUserPortrait cognitoId={a.cognitoId} firstName={firstName} lastName={lastName} />,
          firstName,
          lastName,
        };
      })
      .sort(attendanceSortFn);
  }, [data, data?.attendance, data?.team, data?.team?.teamMembers]);

  if (isLoading) {
    return <Loading message="Fetching meeting..." />;
  }

  if (error || data === null) {
    return getErrorComponentFromHttpError(error);
  }

  const isEditable =
    ['MISSED', 'ENDED'].includes(data.status) && (!isAdministratorView || groups.includes('administrators'));
  const isStartable = ['OVERDUE', 'UPCOMING'].includes(data.status);
  const isTeamLead = currentUser.teams.filter((t) => t.id === teamId)?.[0]?.isLead;

  return (
    <>
      {isEditable &&
        ((isAdministratorView && groups.includes('administrators') && (
          <>
            <AdministratorEditMeetingStatusDialog
              meeting={data}
              isOpen={isEditMeetingStatusDialogOpen}
              close={() => setIsEditMeetingStatusDialogOpen(false)}
              reload={reload}
            />
            <AdministratorEditMeetingAttendanceDialog
              meeting={data}
              isOpen={isEditMeetingAttendanceDialogOpen}
              close={() => setIsEditMeetingAttendanceDialogOpen(false)}
              reload={reload}
            />
            {selectedCommitment && (
              <AdministratorEditMeetingCommitmentDialog
                meeting={data}
                commitment={selectedCommitment}
                isOpen={isEditMeetingCommitmentDialogOpen}
                close={() => {
                  setIsEditMeetingCommitmentDialogOpen(false);
                  setSelectedCommitment(null);
                }}
                reload={reload}
              />
            )}
          </>
        )) || (
          <>
            <TeamEditMeetingStatusDialog
              meeting={data}
              isOpen={isEditMeetingStatusDialogOpen}
              close={() => setIsEditMeetingStatusDialogOpen(false)}
              reload={reload}
            />
            <TeamEditMeetingAttendanceDialog
              meeting={data}
              isOpen={isEditMeetingAttendanceDialogOpen}
              close={() => setIsEditMeetingAttendanceDialogOpen(false)}
              reload={reload}
            />
            {selectedCommitment && (
              <TeamEditMeetingCommitmentDialog
                meeting={data}
                commitment={selectedCommitment}
                isOpen={isEditMeetingCommitmentDialogOpen}
                close={() => {
                  setIsEditMeetingCommitmentDialogOpen(false);
                  setSelectedCommitment(null);
                }}
                reload={reload}
              />
            )}
          </>
        ))}
      {formAlert && <FormAlert alert={formAlert} />}
      <div className={styles.meeting}>
        <div className={styles.header}>
          <div className={styles.details}>
            {(useSecondaryHeadingLevel && (
              <h2>
                {data.team.label} Meeting{' '}
                <span className={styles.meetingDate}>
                  (
                  {new Date(data.startTime ? data.startTime : data.scheduledTime).toLocaleDateString('en-GB', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    weekday: 'long',
                  })}
                  )
                </span>
              </h2>
            )) || (
              <h1>
                {data.team.label} Meeting (
                {(data.startTime && getAbsoluteTimestamp(new Date(data.startTime))) ||
                  getAbsoluteTimestamp(new Date(data.scheduledTime))}
                )
              </h1>
            )}
            <div className={styles.info}>
              <div className={styles.team}>
                <GroupsOutlined />
                {(isAdministratorView && (
                  <Link to={`/administrator/teams/${teamId}`} className={styles.team}>
                    {data.team.label}
                  </Link>
                )) || <p className={styles.team}>{data.team.label}</p>}
              </div>
              <p>
                <span className={styles.label}>Scheduled for:</span>{' '}
                {getAbsoluteTimestamp(new Date(data.scheduledTime), true, true)}
              </p>
              <p>
                <span className={styles.label}>Started:</span>{' '}
                {data.startTime ? getAbsoluteTimestamp(new Date(data.startTime), true, true) : '-'}
              </p>
              <p>
                <span className={styles.label}>Ended:</span>{' '}
                {data.endTime ? getAbsoluteTimestamp(new Date(data.endTime), true, true) : '-'}
              </p>
            </div>
          </div>
          <div className={styles.statusAndControls}>
            <EnumElement enumObject={MEETING_STATUS} value={data.status} />
            <div className={styles.row}>
              {isStartable && isTeamLead && (
                <LoadingButton
                  variant="outlined"
                  endIcon={<EventAvailableOutlinedIcon />}
                  onClick={onStartMeeting}
                  sx={{ backgroundColor: 'rgba(255, 255, 255, .75)' }}
                  loading={isSubmittingStartMeeting}
                >
                  Start Meeting
                </LoadingButton>
              )}
              <Button
                variant="outlined"
                startIcon={<EditCalendarOutlined />}
                onClick={() => setIsEditMeetingStatusDialogOpen(true)}
                disabled={!isEditable}
              >
                Change Status
              </Button>
            </div>
          </div>
        </div>
        <div>
          <Table
            key={uuid()}
            rows={attendanceData}
            keyColumn="cognitoId"
            title={`Attendance`}
            topButtons={[
              {
                label: 'Edit Attendance',
                variant: 'outlined',
                startIcon: <HowToRegOutlinedIcon />,
                handler: isEditable ? () => setIsEditMeetingAttendanceDialogOpen(true) : null,
              },
            ]}
            columns={[
              {
                id: 'portrait',
                label: '',
                component: true,
                shrink: true,
              },
              {
                id: 'firstName',
                label: 'First Name',
              },
              {
                id: 'lastName',
                label: 'Last Name',
              },
              {
                id: 'attendance',
                label: 'Attendance',
                enum: ATTENDANCE_STATUS,
              },
            ]}
          />
        </div>
        <div className={styles.commitments}>
          <h2>Commitments</h2>
          {attendanceData.map(({ cognitoId, firstName, lastName, attendance }) => {
            const commitments = data.commitments
              .filter((c) => c.cognitoId === cognitoId)
              .map((c) => ({
                ...c,
                commitment: (
                  <CommitmentsTableCommitment
                    isGoalAgnostic={c.isGoalAgnostic}
                    label={c.label}
                    note={c.note}
                    goal={c.goal?.label || null}
                    startValue={c.startValue}
                    endValue={c.endValue}
                  />
                ),
              }));
            return (
              <CommitmentsTeamMember
                key={uuid()}
                cognitoId={cognitoId}
                firstName={firstName}
                lastName={lastName}
                commitments={commitments}
                attendance={attendance}
                isEditable={isEditable}
                addCommitment={() => {
                  setSelectedCommitment({
                    label: '',
                    note: '',
                    startValue: '',
                    endValue: '',
                    value: '',
                    isAligned: false,
                    isTimely: false,
                    isImpactful: false,
                    isSpecific: false,
                    goalId: null,
                    cognitoId,
                    meetingId,
                    teamId,
                  });
                  setIsEditMeetingCommitmentDialogOpen(true);
                }}
                editCommitment={(commitment) => {
                  setSelectedCommitment(commitment);
                  setIsEditMeetingCommitmentDialogOpen(true);
                }}
              />
            );
          })}
        </div>
      </div>
    </>
  );
}
